// A checklist that writes into one editable text box: the ticked items become one natural sentence
// ("Em a, b và c."), rebuilt in place on every tick; the teacher's own words around it are left alone.

export interface SentenceRule {
  // said once at the start; a leading copy on an item is dropped
  subject: string;
  // checklist order, which the sentence follows whatever the tick order
  order: readonly string[];
}

export interface ChecklistText {
  ticks: string[];
  text: string;
  // ticks the generated sentence in `text` still spells; ticks made after the teacher edited it stand alone
  owned: string[];
}

/** Items in checklist order, unknown ones after them, each once. */
export function orderItems(order: readonly string[], items: readonly string[]): string[] {
  const known = order.filter((item) => items.indexOf(item) >= 0);
  return known.concat(items.filter((item, index) => order.indexOf(item) < 0 && items.indexOf(item) === index));
}

function clause(subject: string, item: string): string {
  const value = item.trim().replace(/\.+$/, '');
  const lead = `${subject.toLowerCase()} `;
  const body = value.toLowerCase().indexOf(lead) === 0 ? value.slice(lead.length).trim() : value;
  return body.charAt(0).toLowerCase() + body.slice(1);
}

/** "Em học tập tốt, rất tích cực phát biểu và có tiến bộ rõ rệt."; '' when nothing is ticked. */
export function composeSentence(rule: SentenceRule, items: readonly string[]): string {
  const clauses = orderItems(rule.order, items).map((item) => clause(rule.subject, item)).filter(Boolean);
  if (clauses.length === 0) return '';
  const last = clauses[clauses.length - 1];
  const body = clauses.length === 1 ? last : `${clauses.slice(0, -1).join(', ')} và ${last}`;
  return `${rule.subject} ${body}.`;
}

function appendSentence(text: string, sentence: string): string {
  const head = text.replace(/\s+$/, '');
  if (!sentence) return head;
  return head ? `${head} ${sentence}` : sentence;
}

// replaces text[at, at + length); an empty replacement closes the gap it leaves, keeping the teacher's line breaks
function splice(text: string, at: number, length: number, replacement: string): string {
  if (replacement) return text.slice(0, at) + replacement + text.slice(at + length);
  const before = text.slice(0, at).replace(/[ \t]+$/, '');
  let after = text.slice(at + length).replace(/^[ \t]+/, '');
  // a sentence on a line of its own takes its line break with it
  if (before.charAt(before.length - 1) === '\n' && after.charAt(0) === '\n') after = after.slice(1);
  const joint = before && after && before.charAt(before.length - 1) !== '\n' && after.charAt(0) !== '\n' ? ' ' : '';
  return (before + joint + after).trim();
}

function without(items: readonly string[], item: string): string[] {
  return items.filter((value) => value !== item);
}

/** Ticks or unticks `item`, editing only the generated sentence; text the teacher changed is never removed. */
export function toggleItem(state: ChecklistText, rule: SentenceRule, item: string): ChecklistText {
  const generated = composeSentence(rule, state.owned);
  const at = generated ? state.text.indexOf(generated) : -1;
  if (state.ticks.indexOf(item) < 0) {
    const ticks = state.ticks.concat(item);
    if (at >= 0) {
      const owned = state.owned.concat(item);
      return { ticks, owned, text: splice(state.text, at, generated.length, composeSentence(rule, owned)) };
    }
    return { ticks, owned: [item], text: appendSentence(state.text, composeSentence(rule, [item])) };
  }
  const ticks = without(state.ticks, item);
  const owned = without(state.owned, item);
  if (at >= 0 && state.owned.indexOf(item) >= 0) return { ticks, owned, text: splice(state.text, at, generated.length, composeSentence(rule, owned)) };
  const alone = composeSentence(rule, [item]);
  const found = state.text.indexOf(alone);
  return { ticks, owned, text: found >= 0 ? splice(state.text, found, alone.length, '') : state.text };
}

/** A stored text and its ticks; the ticks own the sentence only while the text still spells it. */
export function restoreChecklist(rule: SentenceRule, ticks: readonly string[], text: string): ChecklistText {
  const ordered = orderItems(rule.order, ticks);
  const generated = composeSentence(rule, ordered);
  return { ticks: ordered, text, owned: generated && text.indexOf(generated) >= 0 ? ordered : [] };
}

export const EMPTY_CHECKLIST: ChecklistText = { ticks: [], text: '', owned: [] };
